import { useState, useEffect, useMemo } from 'react';
import { format, subYears } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBlockingLoader } from 'shared';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import CrossBorderWithdrawalContainer from './Details/CrossBorderWithdrawalContainer';
import EftFundingTransactionContainer from './Details/EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './Details/EftWithdrawTransactionContainer';
import AchFundingTransactionContainer from './Details/ACHFundingTransactionContainer';
import AchWithdrawalTransactionContainer from './Details/ACHWithdrawalTransactionContainer';
import InteracMoneyRequestTransactionContainer from './Details/InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './Details/InteracBulkPayoutTransactionContainer';
import VopayInstantWithdrawalContainer from './Details/VopayInstantWithdrawalContainer';
import RefundTransactionContainer from './Details/RefundTransactionContainer';
import CurrencyConversionTransactionContainer from './Details/CurrencyConversionTransactionContainer';
import InboundETransferContainer from './Details/InboundETransferContainer';
import VisaDirectPushTransactionContainer from './Details/VisaDirectPushTransactionContainer';
import CreditCardTransactionContainer from './Details/CreditCardTransactionContainer';
import ReversalTransactionContainer from './Details/ReversalTransactionContainer';
import RTPWithdrawalContainer from './Details/RTPWithdrawalContainer';
import FedwireFundingContainer from './Details/FedwireFundingContainer';
import FedwireWithdrawalContainer from './Details/FedwireWithdrawalContainer';
import OtherTransactionContainer from './Details/OtherTransactionContainer';
import AutoBalanceTransferTransactionContainer from './Details/AutoBalanceTransferTransactionContainer';
import DigitalWalletTransactionContainer from './Details/DigitalWalletTransactionContainer';
import ZelleTransactionContainer from './Details/ZelleTransactionContainer';
import AccountTransferTransactionInfo from './Details/AccountTransferTransactionInfo';
import useTransactionRefund from '@/hooks/useApi/useTransactionRefund';
import useTransactionCancel from '@/hooks/useApi/useTransactionCancel';
import useCancelInteracMoneyRequest from '@/hooks/useApi/useCancelInteracMoneyRequest';
import useCancelVoPayInstant from '@/hooks/useApi/useCancelVoPayInstant';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import useCancelInteracBulkPayout from '@/hooks/useApi/useCancelInteracBulkPayout';
import useVoidACHTransaction from '@/hooks/useApi/useVoidACHTransaction';
import useRefundACHTransaction from '@/hooks/useApi/useRefundACHTransaction';
import useRefundCreditCardTransaction from '@/hooks/useApi/useRefundCreditCardTransaction';
import useVoidCreditCardTransaction from '@/hooks/useApi/useVoidCreditCardTransaction';
import useElinx from '@/components/Elinx/ElinxHistory/Details/useElinx';
import useZelle from '@/hooks/useApi/useZelle';
import styles from './Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import FundingStatusBanner from './FundingStatusBanner/FundingStatusBanner';
import FundingReverseBanner from './Details/FundingReverseBanner/FundingReverseBanner';
import { NON_PAYMENT_TYPES } from './Details/isPaymentSubTransaction';
import { useConfirmDialog } from '@/providers/ConfirmDialog.provider';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { Box, DropdownMenu, TransactionType, TransactionTypesOptions, toBool } from 'vopay';
import { UtilButton, Title, Button } from 'UI';
import BillPayContainer from './Details/BillPayContainer';
import ElinxTransactionInfo from './Details/ElinxTransactionInfo';
import ResubmitTransactionButton from './ResubmitTransactionButton';
import { TransactionDetailInfo } from './Details/transactionDetail.types';
import * as transactionDetailRoute from './transactionDetail.route';

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  clientAccountId: string;
};
type URLParams = {
  TransactionType: string;
  TransactionID: string;
  Currency: string;
};

export default function TransactionDetails() {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionDetails' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { t: tRefundDialog } = useTranslation('translations', {
    keyPrefix: 'Transaction.Dialogue.RefundTransaction.RefundTransactionDialogue',
  });

  const { t: tCancelDialog } = useTranslation('translations', {
    keyPrefix: 'Transaction.Dialogue.CancelTransaction.CancelTransactionDialogue',
  });

  const alertDialog = useAlertDialog();
  const toast = useToast();
  const confirmDialog = useConfirmDialog();
  const blockingLoader = useBlockingLoader();
  const history = useHistory();
  const { response } = useClientData();
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const { onSubNavClick } = useNavMenu();
  const { printFile, downloadPdfPage } = useFileDownload();
  const [transactionId, setTransactionId] = useState('');
  const [hasNewTransaction, setHasNewTransaction] = useState(false);

  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  // Shared by FundingStatusBanner and the rail containers' Credit Card fallback — resolved once
  // here instead of each of them independently fetching the same parent transaction by ID.
  const { transactions: genericParentTransactions, fetchTransactionsHistory: fetchGenericParentTransaction } =
    useFetchTransactionsHistory();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const [jwt, setJwt] = useState('');

  const currentTransaction = useMemo(() => {
    return transactions.find((transaction) => transaction.TransactionID === transactionId);
  }, [transactionId, transactions]);

  const detailInfo = useMemo<TransactionDetailInfo>(
    () => ({
      failureReason: currentTransaction?.TransactionFailureReason ?? '',
      accountId: currentTransaction?.ClientAccountID ?? '',
      hasErrorInfo: currentTransaction?.TransactionStatus === 'failed',
    }),
    [currentTransaction],
  );

  const isMainAccount = !currentTransaction?.AccountID || currentTransaction.AccountID === response.AccountID;
  const { voidCreditCardTransaction } = useVoidCreditCardTransaction({ bearer: jwt });
  const { cancelElinx } = useElinx();
  const { cancelZelleWithdraw } = useZelle({ bearer: jwt });
  const { transactionRefund } = useTransactionRefund({ bearer: jwt });
  const { transactionCancel } = useTransactionCancel({ bearer: jwt });
  const { cancelInteracBulkPayout } = useCancelInteracBulkPayout({ bearer: jwt });
  const { voidACHTransaction } = useVoidACHTransaction({ bearer: jwt });
  const { refundACHTransaction } = useRefundACHTransaction({ bearer: jwt });
  const { refundCreditCardTransaction } = useRefundCreditCardTransaction({ bearer: jwt });
  const { cancelInteracMoneyRequest } = useCancelInteracMoneyRequest({ bearer: jwt });
  const { cancelVoPayInstant } = useCancelVoPayInstant({ bearer: jwt });
  const { permissions } = usePermissions();

  const { transactionsReset, approvalListReset } = useApprovalWorkflow();

  const transactionType = useMemo(() => {
    return currentTransaction?.TransactionType;
  }, [currentTransaction]);

  const transactionStatus = useMemo(() => {
    return currentTransaction?.TransactionStatus;
  }, [currentTransaction]);

  const transactionID = useMemo(() => {
    return currentTransaction?.TransactionID || transactionId;
  }, [currentTransaction, transactionId]);

  // FundingReverseBanner only makes sense on a funding transaction's own page — a Refund or
  // reversal also sets a child's ParentTransactionID back to this transaction, so without this
  // gate the banner would render on unrelated transaction types too.
  const isFundingParent =
    transactionType === TransactionType.EFT_FUNDING ||
    transactionType === TransactionType.ACH_FUNDING ||
    transactionType === TransactionType.CREDIT_CARD;

  // The child family is already in `transactions` (fetched by TransactionID). Skip Fee/reversal/
  // bank-account children — same exclusion list as isPaymentSubTransaction, for a differently
  // typed TransactionType field. Also skip a Collection's own "Deposit Collected Funds To" bank
  // sweep — same ParentTransactionID shape as a funded payment, but it isn't one.
  const subTransaction = useMemo(() => {
    if (!isFundingParent) return undefined;

    return transactions.find(
      (transaction) =>
        transaction.ParentTransactionID === currentTransaction?.TransactionID &&
        !NON_PAYMENT_TYPES.includes(transaction.TransactionType) &&
        transaction.Notes !== 'Deposit Collected Funds',
    );
  }, [transactions, currentTransaction, isFundingParent]);

  const subTransactionId = subTransaction?.TransactionID;

  // Generic parent lookup by ID — fired whenever a ParentTransactionID exists, independent of this
  // transaction's own status. FundingStatusBanner only needs it while waiting, but the rail
  // containers' Credit Card fallback need it regardless of status, so this can't be gated narrower.
  useEffect(() => {
    if (!currentTransaction?.ParentTransactionID) return;

    const loadGenericParentTransaction = async () => {
      try {
        await fetchGenericParentTransaction({
          StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
          EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
          TransactionID: currentTransaction.ParentTransactionID,
        });
      } catch {
        toast.neutral(t('fetchFundingParentError', { defaultValue: 'Could not load the funding transaction' }));
      }
    };

    loadGenericParentTransaction();
  }, [currentTransaction?.ParentTransactionID]);

  const genericParentTransaction = useMemo(
    () => genericParentTransactions.find((t) => t.TransactionID === currentTransaction?.ParentTransactionID),
    [genericParentTransactions, currentTransaction?.ParentTransactionID],
  );

  async function handleRefund() {
    const result = await confirmDialog.open(
      tRefundDialog('confirmDialogue.title', { defaultValue: 'Are you sure you want to refund this transaction?' }),
      {
        confirmLabel: tRefundDialog('confirmDialogue.yes', { defaultValue: 'Yes' }),
        cancelLabel: tRefundDialog('confirmDialogue.no', { defaultValue: 'No' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      blockingLoader.show();

      const refund = () => {
        if (transactionType === 'Credit Card') return refundCreditCardTransaction({ TransactionID: transactionId });
        if (response.Country === 'US') return refundACHTransaction({ TransactionID: transactionId });

        return transactionRefund({ TransactionID: transactionId });
      };

      const { TransactionID: refundedId } = await refund();

      history.push(
        transactionDetailRoute.getPath({
          transactionType: 'Refund',
          transactionID: refundedId,
          currency: transactions[0]?.Currency ?? '',
        }),
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tRefundDialog('errorDialogue.title', { defaultValue: 'Error! The transaction was not refunded' }),
        errorMessage,
      );
    } finally {
      blockingLoader.hide();
    }
  }

  function navigateAfterCancel(newTransactionId: string, transactionType: TransactionTypesOptions) {
    // Push to the new transaction's detail URL; the [TransactionID] param effect resets + refetches from
    // there. No setTransactionId/setHasNewTransaction here — that effect does both — and no
    // window.location.reload(): a hard reload would tear down the SPA and cancel this navigation.
    history.push(
      transactionDetailRoute.getPath({
        transactionType,
        transactionID: newTransactionId,
        currency: transactions[0]?.Currency ?? '',
      }),
    );
  }

  async function handleCancel() {
    const result = await confirmDialog.open(
      tCancelDialog('confirmDialogue.title', { defaultValue: 'Are you sure you want to void this transaction?' }),
      {
        confirmLabel: tCancelDialog('confirmDialogue.yes', { defaultValue: 'Yes' }),
        cancelLabel: tCancelDialog('confirmDialogue.no', { defaultValue: 'No' }),
      },
    );

    if (result !== 'confirm') return;

    const isCancellableStatus =
      transactionStatus === 'in progress' || transactionStatus === 'pending' || transactionStatus === 'sent';

    try {
      blockingLoader.show();

      if (transactionType === 'Interac Money Request' && isCancellableStatus) {
        const { TransactionID: cancelledId } = await cancelInteracMoneyRequest({ TransactionID: transactionId });
        navigateAfterCancel(cancelledId, transactions[0].TransactionType);
      } else if (transactionType === 'Interac Bulk Payout' && isCancellableStatus) {
        const { TransactionID: cancelledId } = await cancelInteracBulkPayout({ TransactionID: transactionId });
        navigateAfterCancel(cancelledId, transactions[0].TransactionType);
      } else if (transactionType === 'ACH Funding' || transactionType === 'ACH Withdrawal') {
        const { TransactionID: voidedId } = await voidACHTransaction({ TransactionID: transactionId });
        navigateAfterCancel(voidedId, transactions[0].TransactionType);
      } else if (transactionType === 'VoPayInstant Withdraw' && isCancellableStatus) {
        const { TransactionID: cancelledId } = await cancelVoPayInstant({ TransactionID: transactionId });
        navigateAfterCancel(cancelledId, transactions[0].TransactionType);
      } else if (transactionType === 'Credit Card' && transactionStatus === 'successful') {
        const { TransactionID: voidedId } = await voidCreditCardTransaction({ TransactionID: transactionId });
        navigateAfterCancel(voidedId, transactions[0].TransactionType);
      } else if (transactionType === 'eLinx') {
        await cancelElinx({ ELinxRequestID: currentTransaction?.ELinxRequestID || '' });
        setHasNewTransaction(true);
      } else if (transactionType === 'Zelle Withdrawal') {
        await cancelZelleWithdraw({ TransactionID: transactionId });
        setHasNewTransaction(true);
      } else {
        const { TransactionID: cancelledId } = await transactionCancel({ TransactionID: transactionId });
        navigateAfterCancel(cancelledId, transactions[0].TransactionType);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tCancelDialog('errorDialogue.title', { defaultValue: 'Error! The transaction was not voided' }),
        errorMessage,
      );
    } finally {
      blockingLoader.hide();
    }
  }

  useEffect(() => {
    setTransactionId(TransactionID);
    setHasNewTransaction(true);
    transactionsReset();
    approvalListReset();
  }, [TransactionID]);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!transactionId || !hasNewTransaction) return;

    const load = async () => {
      try {
        blockingLoader.show();

        const res = await fetchTransactionsHistory({
          StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
          EndDateTime: new Date().toISOString().slice(0, 10),
          TransactionID,
          IncludeSubaccountTransactions: true,
        });

        if (!res.Success) throw new Error(res.ErrorMessage);
        setInitialized(true);
      } catch {
        alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
      } finally {
        blockingLoader.hide();
      }
    };

    setHasNewTransaction(false);
    load();
  }, [transactionId, hasNewTransaction]);

  useEffect(() => {
    if (!currentTransaction?.AccountID) return;

    if (currentTransaction.AccountID === response.AccountID) {
      setJwt(response.JWT || '');

      return;
    }

    const loadToken = async () => {
      try {
        blockingLoader.show();

        const res = await fetchSubAccountsAsync({
          SubaccountID: currentTransaction.AccountID,
          ShowToken: true,
        });

        if (!res.Success) throw new Error(res.ErrorMessage);
        const match = Object.values(res.Subaccounts || {}).find((s) => s.AccountID === currentTransaction.AccountID);
        setJwt(match?.Token ?? '');
      } catch {
        alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
      } finally {
        blockingLoader.hide();
      }
    };

    loadToken();
  }, [currentTransaction?.AccountID]);

  const title =
    transactionType === 'Reversal'
      ? t('returnOverview', { defaultValue: 'Return Transaction Overview' })
      : transactionType === 'Account Transfer'
        ? t('transferOverview', { defaultValue: 'Transfer Overview' })
        : t('transactionOverview', { defaultValue: 'Transaction Overview' });

  return (
    <div className={styles.wrapper}>
      <Box flx="col" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/transactions-history');
            }
          }}
        >
          {tGlobal('Buttons.back', { defaultValue: 'Back' })}
        </UtilButton>

        <Box width="100%" flx="row" jc="sb" maxheight="43px">
          <Box jc="fs" ai="center" gap="common">
            <Title>{title}</Title>

            {state?.hasNewTransactionButton && (
              <Button
                id="start-new-transactions"
                colorScheme="primary"
                onClick={() => {
                  if (state.activeModule === 'fundMyAccount') {
                    onSubNavClick(
                      'fundMyAccount',
                      '/fund-my-account',
                      t('fundMyAccount', { defaultValue: 'Fund My Account' }),
                    );
                  } else if (state.activeModule === 'bankAccountTransfer') {
                    onSubNavClick(
                      'bankAccountTransfer',
                      '/bank-account-transfer',
                      t('bankAccountTransfer', { defaultValue: 'Bank Account Transfer' }),
                    );
                  } else if (state.activeModule === 'withdrawMyAccount') {
                    onSubNavClick(
                      'withdrawMyAccount',
                      '/withdraw-my-account',
                      t('withdrawMyAccount', { defaultValue: 'Withdraw My Account' }),
                    );
                  } else if (state.activeModule === 'singleCollection') {
                    onSubNavClick(
                      'singleCollection',
                      '/single-collection',
                      t('collection', { defaultValue: 'Collection' }),
                    );
                  } else if (state.activeModule === 'singlePayment') {
                    onSubNavClick('singlePayment', '/single-payment', t('payment', { defaultValue: 'Payment' }));
                  } else if (state.activeModule === 'accountTransfer') {
                    onSubNavClick(
                      'accountTransfer',
                      '/sub-account-transfer',
                      t('accountTransfer', { defaultValue: 'Account Transfer' }),
                    );
                  }
                }}
              >
                {t('startNew', { defaultValue: 'Start New Transaction' })}
              </Button>
            )}
          </Box>

          <Box flx="row" gap="sm">
            {((transactionType === 'EFT Funding' &&
              transactionStatus === 'successful' &&
              currentTransaction?.IsRefunded === false) ||
              (transactionType === 'EFT Withdrawal' &&
                transactionStatus === 'successful' &&
                currentTransaction?.IsRefunded === false) ||
              (transactionType === 'Credit Card' &&
                (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                currentTransaction?.IsRefunded === false) ||
              (transactionType === 'ACH Funding' &&
                (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                currentTransaction?.IsRefunded === false) ||
              (transactionType === 'ACH Withdrawal' &&
                (transactionStatus === 'settled' || transactionStatus === 'successful') &&
                currentTransaction?.IsRefunded === false)) &&
            isMainAccount &&
            permissions.ManageTransactions ? (
              <Button id="refund-transaction" color="secondary" onClick={handleRefund}>
                {t('refund', { defaultValue: 'Refund' })}
              </Button>
            ) : (
              <></>
            )}

                  {transactionStatus === 'pending approval' &&
                    (transactionType === 'EFT Funding' ||
                      transactionType === 'EFT Withdrawal' ||
                      transactionType === 'VoPayInstant Withdraw' ||
                      transactionType === 'Interac Money Request') &&
                    permissions.ManageTransactions && (
                      <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                        {tGlobal('cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    )}

                  {transactionType === 'eLinx' &&
                    (transactionStatus === 'pending' || transactionStatus === 'pending approval') &&
                    permissions.ManageTransactions && (
                      <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                        {tGlobal('cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    )}

                  {transactionType === 'Credit Card' &&
                  transactionStatus === 'successful' &&
                  permissions.ManageTransactions ? (
                    <Button id="void-credit-card-transaction" color="secondary" onClick={handleCancel}>
                      {tGlobal('cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'EFT Funding' &&
                    (transactionStatus === 'pending' ||
                      (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'EFT Withdrawal' &&
                      (transactionStatus === 'pending' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Interac Money Request' &&
                      (transactionStatus === 'requested' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Interac Bulk Payout' &&
                      (transactionStatus === 'requested' ||
                        (transactionStatus === 'waiting' && toBool(response.ClientAccountsEnabled)))) ||
                    (transactionType === 'Account Transfer' && transactionStatus === 'waiting')) &&
                  isMainAccount &&
                  permissions.ManageTransactions ? (
                    <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                      {t('cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'ACH Funding' && transactionStatus === 'pending') ||
                    (transactionType === 'ACH Withdrawal' && transactionStatus === 'pending')) &&
                  isMainAccount &&
                  permissions.ManageTransactions ? (
                    <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                      {t('void', { defaultValue: 'Void' })}
                    </Button>
                  ) : (
                    <></>
                  )}
                  {((transactionType === 'Interac Money Request' &&
                    (transactionStatus === 'in progress' ||
                      transactionStatus === 'pending' ||
                      transactionStatus === 'sent' ||
                      transactionStatus === 'waiting')) ||
                    (transactionType === 'Interac Bulk Payout' &&
                      (transactionStatus === 'in progress' ||
                        transactionStatus === 'pending' ||
                        transactionStatus === 'sent' ||
                        transactionStatus === 'waiting')) ||
                    (transactionType === 'VoPayInstant Withdraw' &&
                      (transactionStatus === 'in progress' ||
                        transactionStatus === 'pending' ||
                        transactionStatus === 'sent' ||
                        transactionStatus === 'waiting'))) &&
                  isMainAccount &&
                  permissions.ManageTransactions ? (
                    <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                      {t('Buttons.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}

                  {transactionType === 'Zelle Withdrawal' &&
                  (transactionStatus === 'pending' ||
                    transactionStatus === 'waiting' ||
                    transactionStatus === 'pending approval' ||
                    transactionStatus === 'in progress') &&
                  isMainAccount &&
                  permissions.ManageTransactions ? (
                    <Button id="cancel-transaction" color="secondary" onClick={handleCancel}>
                      {tGlobal('cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  ) : (
                    <></>
                  )}

                  {toBool(response.TransactionResubmissionEnabled) && transactionType && currentTransaction && jwt && (
                    <ResubmitTransactionButton
                      key={transactionID}
                      transactionId={transactionID}
                      transactionType={transactionType}
                      currency={currentTransaction.Currency}
                      bearer={jwt}
                    />
                  )}

                  <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                      <Button iconRight="keyboard_arrow_down">
                        {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
                      </Button>
                    </DropdownMenu.Trigger>

                    <DropdownMenu.Content align="end">
                      <DropdownMenu.Item
                        onSelect={() => {
                          downloadPdfPage('transaction-overview', title);
                        }}
                      >
                        {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={() => {
                          printFile('transaction-overview');
                        }}
                      >
                        {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu>
                </Box>
              </Box>
              {useMemo(
                () => (
                  <>
                    {initialized && jwt && (
                      <div id="transaction-overview" className={styles.overviewWrapper}>
                        <Box flx="col" width="50%">
                          {/* Suppressed for a Collection's own "Deposit Collected Funds To" bank sweep —
                              same ParentTransactionID shape as a funded payment, but it isn't one. */}
                          {currentTransaction?.Notes !== 'Deposit Collected Funds' && (
                            <FundingStatusBanner
                              className={styles.approverPanel}
                              transactionStatus={transactionStatus}
                              parentTransactionId={currentTransaction?.ParentTransactionID}
                              parentTransaction={genericParentTransaction}
                              currency={currentTransaction?.Currency}
                              eLinxRequestId={currentTransaction?.ELinxRequestID}
                            />
                          )}
                          <FundingReverseBanner
                            fundingStatus={transactionStatus}
                            subTransactionId={subTransactionId}
                            subTransactionStatus={subTransaction?.TransactionStatus}
                            contactName={subTransaction?.FullName}
                            eLinxRequestId={subTransaction?.ELinxRequestID}
                          />

                          {currentTransaction?.Notes === 'Scheduled Auto Balance Transfer' ? (
                            <AutoBalanceTransferTransactionContainer
                              transactionID={transactionID}
                              transactionHeader={currentTransaction}
                              jwt={jwt}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.CROSSBORDER_WITHDRAWAL ? (
                            <CrossBorderWithdrawalContainer
                              transactionId={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.EFT_FUNDING ? (
                            <EftFundingTransactionContainer
                              TransactionID={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              eLinxRequestID={
                                currentTransaction?.ELinxRequestID || subTransaction?.ELinxRequestID || ''
                              }
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.EFT_WITHDRAWAL ? (
                            <EftWithdrawTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              parentTransaction={genericParentTransaction}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.ACH_FUNDING ? (
                            <AchFundingTransactionContainer
                              TransactionID={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              eLinxRequestID={
                                currentTransaction?.ELinxRequestID || subTransaction?.ELinxRequestID || ''
                              }
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.ACH_WITHDRAWAL ? (
                            <AchWithdrawalTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              parentTransaction={genericParentTransaction}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.INTERACT_MONEY_REQUEST ? (
                            <InteracMoneyRequestTransactionContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.INTERACT_BULK_PAYOUT ? (
                            <InteracBulkPayoutTransactionContainer
                              TransactionID={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              eLinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              parentTransaction={genericParentTransaction}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.VOPAYINSTANT_WITHDRAWAL ? (
                            <VopayInstantWithdrawalContainer
                              TransactionID={transactionID}
                              notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              eLinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              parentTransaction={genericParentTransaction}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.REFUND ? (
                            <RefundTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.CURRENCY_CONVERSION ? (
                            <CurrencyConversionTransactionContainer TransactionID={transactionID} jwt={jwt} />
                          ) : transactionType === TransactionType.INBOUND_E_TRANSFER ? (
                            <InboundETransferContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.VISA_DIRECT_PUSH ? (
                            <VisaDirectPushTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.RTP_WITHDRAWAL ? (
                            <RTPWithdrawalContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.FEDWIRE_FUNDING ? (
                            <FedwireFundingContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.WIRE ? (
                            <FedwireWithdrawalContainer
                              TransactionID={transactionID}
                              Notes={currentTransaction?.Notes || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.CREDIT_CARD ? (
                            <CreditCardTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              eLinxRequestID={
                                currentTransaction?.ELinxRequestID || subTransaction?.ELinxRequestID || ''
                              }
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.REVERSAL ? (
                            <ReversalTransactionContainer
                              TransactionID={transactionID}
                              jwt={jwt}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.GOOGLE_PAY ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="GooglePay"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.VENMO_WITHDRAWAL ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="Venmo"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.PAYPAL_WITHDRAWAL ? (
                            <DigitalWalletTransactionContainer
                              TransactionID={transactionID}
                              transactionType="PayPal"
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.ZELLE_WITHDRAWAL ? (
                            <ZelleTransactionContainer
                              TransactionID={transactionID}
                              fullName={currentTransaction?.FullName || ''}
                              jwt={jwt}
                              ELinxRequestID={currentTransaction?.ELinxRequestID || ''}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.BILL_PAY ? (
                            <BillPayContainer
                              TransactionID={transactionId}
                              jwt={jwt}
                              parentTransaction={genericParentTransaction}
                              transactionDetail={detailInfo}
                            />
                          ) : transactionType === TransactionType.ACCOUNT_TRANSFER && currentTransaction ? (
                            <AccountTransferTransactionInfo
                              transaction={currentTransaction}
                              transactionID={transactionID}
                              jwt={jwt}
                            />
                          ) : transactionType === TransactionType.ELINX && currentTransaction?.ELinxRequestID ? (
                            <ElinxTransactionInfo
                              eLinxRequestID={currentTransaction.ELinxRequestID}
                              jwt={jwt}
                              parentTransaction={genericParentTransaction}
                            />
                          ) : (
                            <OtherTransactionContainer transactionDetail={detailInfo} />
                          )}
                        </Box>
                      </div>
                    )}
                  </>
                ),
                [transactionId, transactions, jwt, genericParentTransaction],
              )}
      </Box>
    </div>
  );
}
